/**
 * [PURPOSE]: Generic slot-recovery picker. Wizard Arcane Recovery is the
 *   first SRD user; CW custom-class slot-recovery features use the same
 *   component (Rule 12 — CW-replicable).
 * [WHY]: Spec A Pillar 4 — one shared modal. Replaces the Wizard-specific
 *   Arcane Recovery UI I almost wrote.
 * [INTEGRATION]: RestPanel "Class Actions" section iterates
 *   effectCalculator.getSlotRecoveryActions and mounts this modal per
 *   action. Confirm payload = { [slotLevel]: deltaCount } to be applied
 *   to the parent's spellSlots state (decrement `used`).
 * [SPEC]: docs/superpowers/specs/2026-05-16-wizard-capstones-and-generic-feature-mechanics-design.md Pillar 4
 * [PLAN]: docs/superpowers/plans/2026-05-16-wizard-capstones-and-generic-feature-mechanics.md Task 7
 * [RULE 19 / UI-MOBILEMODAL-ADOPTION wave 1, 2026-07-04]: Chrome migrated onto
 *   the shared MobileModal scaffold (max-h/overflow-y on mobile — audit B-3).
 *   MobileModal owns useModalEscape(isOpen, onClose) internally now; onCancel
 *   doubles as MobileModal's onClose. Action buttons (Cancel/Confirm) stay
 *   custom in children — showSaveButton={false}. z-[60] preserves the old
 *   Z.MODAL stacking (MobileModal default is z-50).
 */

import React, { useState, useEffect, useMemo } from 'react';
import MobileModal from './MobileModal';

const ORDINAL_SUFFIXES = { 1: 'st', 2: 'nd', 3: 'rd' };
function ordinal(n) {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  return `${n}${ORDINAL_SUFFIXES[n % 10] || 'th'}`;
}

const SlotRecoveryModal = ({
  isOpen,
  actionName = 'Slot Recovery',
  resolvedBudget = 0,
  maxSlotTier = 5,
  currentSlots = {},
  classLevel = 1,
  // [Rule 11 lift 2026-05-16]: Generic noun overrides. When not provided,
  // copy falls back to the D&D-shaped defaults ("levels of spell slots",
  // "-level" suffix). A CW custom-class slot-recovery declaration can
  // override these to render its own nouns (e.g., "focus points",
  // "circle"), making the modal cross-system-ready without forking.
  poolNounPlural,
  tierNoun,
  onConfirm,
  onCancel,
}) => {
  // allocation: { [slotLevel]: deltaCount }
  const [allocation, setAllocation] = useState({});

  // Reset allocation when the modal opens (e.g., user closed + reopened
  // after a partial allocation).
  useEffect(() => {
    if (isOpen) setAllocation({});
  }, [isOpen]);

  // [UI-MOBILEMODAL-ADOPTION wave 1]: useModalEscape removed here — MobileModal
  // registers it internally via onClose (avoid double registration).

  // Pre-compute per-tier expended counts and the running total of allocation
  // for budget enforcement + UI state.
  const tiers = useMemo(() => {
    const arr = [];
    for (let level = 1; level <= maxSlotTier; level += 1) {
      const slot = currentSlots?.[level] || { max: 0, used: 0 };
      const expended = Math.max(0, (slot.used || 0));
      const max = Math.max(0, (slot.max || 0));
      arr.push({ level, expended, max, allocated: allocation[level] || 0 });
    }
    return arr;
  }, [maxSlotTier, currentSlots, allocation]);

  const totalAllocated = tiers.reduce((sum, t) => sum + t.allocated, 0);
  const budgetRemaining = Math.max(0, resolvedBudget - totalAllocated);

  const handleIncrement = (level) => {
    setAllocation(prev => {
      const current = prev[level] || 0;
      const tier = tiers.find(t => t.level === level);
      const tierMax = tier?.expended ?? 0;
      if (current >= tierMax) return prev;
      if (totalAllocated >= resolvedBudget) return prev;
      return { ...prev, [level]: current + 1 };
    });
  };

  const handleDecrement = (level) => {
    setAllocation(prev => {
      const current = prev[level] || 0;
      if (current <= 0) return prev;
      const next = { ...prev };
      if (current === 1) delete next[level];
      else next[level] = current - 1;
      return next;
    });
  };

  const canConfirm = totalAllocated > 0;

  return (
    <MobileModal
      isOpen={isOpen}
      onClose={onCancel}
      title={actionName}
      showSaveButton={false}
      overlayClassName="z-[60]"
    >
      <p className="text-xs text-gray-400 mb-4">
        {/* [Rule 11 lift 2026-05-16]: read picker.poolNounPlural + tierNoun
            so a CW custom-class slot-recovery (e.g., "focus points",
            "luck dice") renders its own noun. Defaults preserve Artificer/
            Wizard SRD behavior. */}
        Allocate up to <span className="font-semibold text-amber-300">{resolvedBudget}</span> {poolNounPlural || 'levels of spell slots'}
        to recover (none higher than {ordinal(maxSlotTier)}{tierNoun ? '' : '-level'}{tierNoun ? ` ${tierNoun}` : ''}).
      </p>
      <p className="text-sm text-amber-200 mb-4">
        Budget remaining: <span className="font-bold">{budgetRemaining}</span> / {resolvedBudget}
      </p>

      <div className="space-y-2">
        {tiers.map(({ level, expended, max, allocated }) => {
          const cannotAdd = expended === 0 || allocated >= expended || budgetRemaining <= 0;
          const cannotSub = allocated <= 0;
          return (
            <div
              key={level}
              className="flex items-center justify-between p-2 bg-gray-900/70 border border-gray-700 rounded"
            >
              <span className="text-gray-200 text-sm">
                {ordinal(level)}-level slot
                {expended === 0 ? (
                  <span className="text-gray-600 text-xs ml-2">(none expended)</span>
                ) : (
                  <span className="text-amber-400 text-xs ml-2">{expended} expended / {max} max</span>
                )}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  aria-label={`Remove ${ordinal(level)}-level slot from allocation`}
                  onClick={() => handleDecrement(level)}
                  disabled={cannotSub}
                  className="w-7 h-7 rounded bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-600 text-white text-sm font-bold disabled:cursor-not-allowed"
                >−</button>
                <span className="text-white text-sm font-mono w-6 text-center">{allocated}</span>
                <button
                  type="button"
                  aria-label={`Add ${ordinal(level)}-level slot to allocation`}
                  onClick={() => handleIncrement(level)}
                  disabled={cannotAdd}
                  className="w-7 h-7 rounded bg-purple-700 hover:bg-purple-600 disabled:bg-gray-800 disabled:text-gray-600 text-white text-sm font-bold disabled:cursor-not-allowed"
                >+</button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex gap-2 mt-6 justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => onConfirm(allocation)}
          disabled={!canConfirm}
          className="px-4 py-2 bg-amber-600 hover:bg-amber-500 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded transition-colors focus:outline-none focus:ring-2 focus:ring-amber-400 disabled:cursor-not-allowed"
        >
          Confirm
        </button>
      </div>
    </MobileModal>
  );
};

export default SlotRecoveryModal;
